import defaultsDeep from 'lodash/defaultsDeep';
import isEqual from 'lodash/isEqual';
import { useContext, useEffect, useState } from 'react';

import { UserAgentContext } from '../UserAgentContext';

import findMatchingQueries from './findMatchingQueries';
import guessUserAgentInfo from './guessUserAgentInfo';

type MediaQueriesToProps = {
  [query: string]: {
    [propName: string]: any;
  };
};

type QueryObj = {
  query: string;
  mediaQueryList: MediaQueryList;
  props: Record<string, unknown>;
};

type Props = {
  [key: string]: any;
};

const mergeProps = (propsArray: Array<Props>) => {
  // `reverse` because this is `defaultsDeep`, so earlier
  // items in the array take precedence, but we want later
  // items to take precedence.
  return defaultsDeep({}, ...propsArray.reverse());
};

const computeInitialPropsFromUserAgent = (
  userAgent: string,
  mediaQueriesToProps: MediaQueriesToProps
) => {
  const userAgentInfo = guessUserAgentInfo(userAgent);
  const mediaQueries = Object.keys(mediaQueriesToProps);
  const matchingQueries = findMatchingQueries(userAgentInfo, mediaQueries);
  const matchingProps = mediaQueries
    .filter(query => matchingQueries[query])
    .map(query => mediaQueriesToProps[query]);
  return mergeProps(matchingProps);
};

const mergeMatchingProps = (queryObjs: Array<QueryObj>) => {
  const matchingProps = queryObjs
    .filter(queryObj => queryObj?.mediaQueryList?.matches)
    .map(queryObj => queryObj.props);
  return mergeProps(matchingProps);
};

// Hook that takes a `mediaQueriesToProps` which is an object whose
// keys are media queries and whose values are objects
// representing props of one or more components. Returns
// a deeply merged object of all props matching the media
// queries defined in `mediaQueriesToProps`.
const useMediaProps = (mediaQueriesToProps: MediaQueriesToProps) => {
  const userAgent: string = useContext(UserAgentContext);
  const initialProps = computeInitialPropsFromUserAgent(
    userAgent,
    mediaQueriesToProps
  );
  const [props, setProps] = useState(initialProps);
  useEffect(() => {
    const mediaQueries = Object.keys(mediaQueriesToProps);
    // Hold onto `query`, `mediaQueryList`, and `props` since `matchMedia`
    // may change how `query` is structured and we need a way
    // to easily reaccess the `props` defined for that query.
    const queryObjs = mediaQueries.map(query => {
      return {
        query,
        mediaQueryList: window?.matchMedia(query),
        props: mediaQueriesToProps[query],
      };
    });
    // Merge all the props for matching media queries together
    // and then set them as the new props.
    const computeProps = () => {
      const newProps = mergeMatchingProps(queryObjs);
      // Perf enhancement / infinite loop prevention.
      if (!isEqual(newProps, props)) {
        setProps(newProps);
      }
    };
    // Listen to changes for each of the media queries.
    // These events only fire when a media query matches,
    // unlike `onresize`.
    queryObjs.forEach(queryObj => {
      if (queryObj?.mediaQueryList?.addListener) {
        queryObj.mediaQueryList.addListener(computeProps);
      }
    });
    // Initial call to compute the props. Without this
    // the props would never be computed unless the user
    // resized their browser to match a media query.
    computeProps();
    // Return the teardown function.
    return () => {
      queryObjs.forEach(queryObj => {
        if (queryObj?.mediaQueryList?.removeListener) {
          queryObj.mediaQueryList.removeListener(computeProps);
        }
      });
    };
  }, [mediaQueriesToProps, props]);
  return props;
};

export default useMediaProps;
